import { useRef, useState } from "react";
import { uploadFile } from "../httpService";

/**
 * Button that uploads an image and inserts a Markdown image tag at the
 * textarea's current cursor position.
 *
 * @param {{
 *   textareaRef: import("react").RefObject<HTMLTextAreaElement>,
 *   value: string,
 *   onChange: (newValue: string) => void,
 *   accept?: string,
 * }} props
 */
const MarkdownImageInsertButton = ({
  textareaRef,
  value,
  onChange,
  accept = "image/jpeg,image/png,image/webp",
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected if needed
    e.target.value = "";

    setUploading(true);
    setError(null);

    try {
      const url = await uploadFile({ file });
      const markdown = `![](${url})`;

      const textarea = textareaRef.current;
      const start = textarea?.selectionStart ?? value.length;
      const end = textarea?.selectionEnd ?? value.length;

      // Insert on its own line for cleaner markdown
      const before = value.slice(0, start);
      const after = value.slice(end);
      const needsLeadingNewline = before.length > 0 && !before.endsWith("\n");
      const needsTrailingNewline = after.length > 0 && !after.startsWith("\n");
      const insertion =
        (needsLeadingNewline ? "\n" : "") + markdown + (needsTrailingNewline ? "\n" : "");

      const newValue = before + insertion + after;
      onChange(newValue);

      // Restore focus and place caret after the inserted markdown
      if (textarea) {
        const newCaret = start + insertion.length;
        // Schedule after React re-render
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCaret, newCaret);
        }, 0);
      }
    } catch (_err) {
      setError("Kunde inte ladda upp bilden");
    } finally {
      setUploading(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
        aria-label="Välj bild att infoga"
      />
      <button
        type="button"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
        className="inline-flex items-center gap-1.5 px-3 py-1 text-sm border border-border-default rounded hover:bg-surface-soft disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {uploading ? (
          <>
            <span
              className="w-3.5 h-3.5 rounded-full border-2 border-transparent border-t-current animate-spin"
              aria-hidden="true"
            />
            Laddar upp…
          </>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            Infoga bild
          </>
        )}
      </button>
      {error && <span className="text-sm text-error">{error}</span>}
    </span>
  );
};

export default MarkdownImageInsertButton;
